import React, { useState } from 'react';
import { Button, TextField, Box, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.tsx';
import Cookies from 'js-cookie';
import { encryptData } from '../utils/cryptoUtils.ts';
import { Slide } from 'react-awesome-reveal';
import { Particles } from '../components/Particles.tsx';
import { styled } from '@mui/material/styles';
import { useThemeContext } from '../contexts/ThemeContext.tsx';
import { FaHome } from "react-icons/fa";

// Animációk definiálása
const keyframes = `
@keyframes shine {
  0% { box-shadow: 0 0 10px #ff4081; }
  50% { box-shadow: 0 0 20px #ff80ab, 0 0 30px #ff4081; }
  100% { box-shadow: 0 0 10px #ff4081; }
}
`;

// Stílusos gomb a HomePage mintájára
const StyledActionButton = styled(Button)(({ theme }) => ({
  padding: '10px 24px',
  borderRadius: '30px',
  backgroundColor: '#ff4081',
  color: '#fff',
  fontWeight: 'bold',
  transition: '0.3s ease',
  boxShadow: '0 0 10px #ff4081',
  animation: 'shine 2s infinite alternate',
  '&:hover': {
    backgroundColor: '#f50057',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px #ff80ab',
  },
}));

// Másodlagos gomb (pl. Sign Up link)
const StyledSecondaryButton = styled(Button)(({ theme }) => ({
  mt: 2,
  textTransform: 'none',
  borderRadius: '20px',
  color: theme.palette.mode === 'dark' ? '#ff80ab' : '#f50057',
  '&:hover': {
    backgroundColor: 'rgba(255, 64, 129, 0.08)',
  },
}));

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  // Állapotok az elfelejtett jelszóhoz
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const navigate = useNavigate();
  const { darkMode } = useThemeContext();

  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Ha jelszócserére van szükség az ideiglenes jelszó miatt
        if (data.requirePasswordChange === "true") {
            setSnackbarMessage('Please update your temporary password immediately in your profile settings.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            // Itt eldöntheted, hogy egyből egy "Change Password" oldalra irányítod, 
            // vagy beengeded, de később rákényszeríted a cserére.
        }

        Cookies.set('token', data.token, { expires: 1 });
        Cookies.set('role', data.role, { expires: 1 });
        Cookies.set('roles', data.roles, { expires: 1 });
        localStorage.setItem('username', encryptData(username));
        localStorage.setItem('email', encryptData(data.email));

        const bannedResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/banned`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${data.token}`,
            'Content-Type': 'application/json',
          },
        });

        const bannedData = await bannedResponse.json();

        if (bannedResponse.ok && bannedData.banned === true) {
          setSnackbarMessage('Your account has been banned.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setOpenDialog(true);
          return;
        }

        if(data.requirePasswordChange !== "true") {
            setSnackbarMessage('Login successful');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        }
        
        setTimeout(() => { navigate('/linklearning'); }, 1500);
      } else {
        setSnackbarMessage(data.message || 'Sign In failed');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred during login');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleLogout = async () => {
    setOpenDialog(false);
    await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json',
      },
    });
    Cookies.remove('token');
    Cookies.remove('role');
    Cookies.remove('roles');
    localStorage.removeItem('username');
    navigate('/login');
  };

  const handleForgotPasswordSubmit = async () => {
    if (!forgotEmail) {
      setSnackbarMessage('Please enter your email address.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await response.json();

      setSnackbarMessage(data.message || 'If your email is registered, a temporary password has been sent.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setForgotPasswordOpen(false);
      setForgotEmail('');
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      <style>{keyframes}</style>
      <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
          <Particles />
        </div>
        
        <Navbar />
        
        <Slide triggerOnce direction="up">
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '400px',
              margin: '0 auto',
              padding: '30px',
              mt: '100px',
              backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              border: `1px solid ${darkMode ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
              boxShadow: darkMode ? '0 8px 32px rgba(255, 0, 0, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          > 
            <Button 
                component={Link} 
                to="/" 
                sx={{ mb: 3, color: darkMode ? '#fff' : 'primary.main', alignSelf: 'center' }}
            >
              <FaHome size="2em" style={{ marginRight: '10px' }} />
              Home
            </Button>

            <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', color: darkMode ? '#fff' : '#333' }}>
              Sign In
            </Typography>

            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                }
              }}
            />
            <TextField
              label="Password"
              variant="outlined"
              fullWidth
              margin="normal"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                }
              }}
            />

            <Typography 
              variant="body2" 
              sx={{ alignSelf: 'flex-end', mt: 1, cursor: 'pointer', color: darkMode ? '#ff80ab' : '#f50057' }}
              onClick={() => setForgotPasswordOpen(true)}
            >
              Forgot Password?
            </Typography>

            <StyledActionButton type="submit" variant="contained" sx={{ mt: 3 }}>
              Sign In
            </StyledActionButton>

            <StyledSecondaryButton component={Link} to="/register" sx={{ mt: 2 }}>
              Don't have an account? Sign Up!
            </StyledSecondaryButton>
          </Box>
        </Slide>
          
        <Dialog 
            open={openDialog} 
            onClose={handleLogout}
            disableScrollLock
        >
          <DialogTitle>Access Denied</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Your account has been banned. Please contact the support team.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLogout} color="primary" autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Elfelejtett jelszó Dialog */}
        <Dialog 
            open={forgotPasswordOpen} 
            onClose={() => setForgotPasswordOpen(false)}
            disableScrollLock
            PaperProps={{
              style: {
                backgroundColor: darkMode ? '#333' : '#fff',
                color: darkMode ? '#fff' : '#000',
              }
            }}
        >
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: darkMode ? '#bbb' : '#666' }}>
              Enter your email address and we will send you a temporary password valid for 24 hours.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              sx={{ 
                mt: 2,
                '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                },
                // Ha szükséges, sötét módban az input színét itt lehet módosítani
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setForgotPasswordOpen(false)} sx={{ color: darkMode ? '#bbb' : '#666' }}>
              Cancel
            </Button>
            <Button onClick={handleForgotPasswordSubmit} variant="contained" sx={{ backgroundColor: '#ff4081', '&:hover': { backgroundColor: '#f50057' } }}>
              Send Password
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default Login;